import React from 'react';

export const ReportageSection: React.FC = () => {
  return (
    <section id="reportagem" className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="prose prose-lg max-w-none">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 font-serif">
              Desumanização noticiada: como o jornalismo hegemônico legitima a letalidade policial no Brasil
            </h2>
            <div className="text-sm text-gray-500 mb-8 border-l-4 border-blue-600 pl-4">
              <p className="font-medium">Por Verônica Serpa</p>
              <p>Publicado em {new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>

          <div className="space-y-8 text-gray-700 leading-relaxed">
            {/* Abertura da reportagem - 6 parágrafos */}
            <p className="text-xl leading-relaxed">
              <strong className="font-semibold">Em 3 de fevereiro de 2024</strong>, José Marcos Nunes da Silva, homem negro, de 45 anos, foi assassinado por agentes das Rondas Ostensivas Tobias de Aguiar (ROTA) da Polícia Militar de São Paulo (PMSP), na periferia de São Vicente (SP). O homem, catador de recicláveis, foi uma das 56 vítimas da segunda versão da Operação Escudo, deflagrada no início daquele ano, após a morte do soldado Samuel Wesley Cosmo.
            </p>

            <p>
              No mesmo dia, a morte de José Marcos foi amplamente noticiada pelos jornais. Na maioria deles, foi descrito como um homem não identificado que teria disparado contra os policiais. A ele, foi atribuído uma arma, drogas, e um caderno de anotação. Os familiares, no entanto, relataram que o homem não tinha envolvimento com atividades criminosas e que, no dia da operação, foi possível ouvi-lo implorar por sua vida. 


            </p>

            <p>
              A Escudo que se originou da morte de Cosmo foi considerada a <a href="https://almapreta.com.br/sessao/cotidiano/operacao-verao-sp-anuncia-fim-da-2a-operacao-mais-letal-desde-o-carandiru/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">2ª ação mais letal</a> da polícia em todo o estado paulista, perdendo apenas para o Massacre do Carandiru, de 1992. Ela sucedeu a primeira Operação Escudo, com 26 óbitos, instaurada em julho de 2023, depois do assassinato do soldado Patrick Reis, também da Rota. A Baixada Santista foi palco para todas essas mortes.

            </p>

            {/* Espaço para imagem */}
            <div className="my-12">
              <div className="w-full h-auto aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/76f136737872d5c167f801f40e2ffc56.jpeg"
                  alt="Comunidade periférica brasileira"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3 italic">
                Movimento negro promove ato em frente à Secretaria de Segurança Pública em repúdio à chacina no Guarujá, no dia 3 de agosto de 2023. Reprodução/Paulo Pinto/Agência Brasil
              </p>
            </div>

            <p>
              Nove anos antes, em Salvador, 12 jovens negros, com idades entre 15 e 28 anos, foram baleados e mortos em uma operação policial no bairro do Cabula. Outras seis pessoas ficaram feridas. O caso foi denunciado pelo Ministério Público do Estado da Bahia (MP-BA) e, atualmente, é alvo de processo judicial que corre em segredo de justiça. 

            </p>

            <p>
              Apesar de ser atualmente reconhecida como "Chacina da Cabula", o episódio já foi noticiado como mais uma ação policial. Nas manchetes, as vítimas foram declaradas como assaltantes de bancos, que teriam atacado a viatura da Rondas Especiais (Rondesp) da Polícia Militar da Bahia. A Procuradoria-Geral da República (PGR), no entanto, indicou indícios de violência policial.

            </p>

            <p>
              Estes e incontáveis outros casos possuem alguns fatores em comum. Todos envolvem ações policiais com número elevado de mortes, cujas vítimas são majoritariamente negras e periféricas. Há outro ponto que une essas histórias: o modo como foram contadas.
            </p>

            {/* Primeiro subtítulo - 10 parágrafos */}
            <h3 className="text-2xl font-semibold text-gray-900 mt-12 mb-6">
              Mortes como política de Estado


            </h3>

            <p>
              As mortes causadas pelas operações citadas acima —e todas as outras ocorridas no país— apresentam um perfil racial nítido. O 19º Anuário Brasileiro de Segurança Pública, do Fórum Brasileiro de Segurança Pública (FBSP), <a href="https://publicacoes.forumseguranca.org.br/items/c3605778-37b3-4ad6-8239-94e4cb236444" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">indica</a> que, em 2024, as pessoas negras somaram 5.119 dos mortos (82%) em decorrência de ação policial. Um segundo levantamento da entidade destaca que, no ano anterior, os agentes de segurança pública mataram apenas pessoas negras em 447 cidades do país. 

            </p>

            <p>
              Mesmo com a marca de mais de 21 mil pessoas vítimas da letalidade policial nos últimos cinco anos, a pena de morte não foi inserida no nosso Código Penal, o que significa que toda pessoa que for presa possui direitos processuais que devem ser garantidos. 

            </p>

            <p>
              Além dos direitos em relação à integridade física e à proteção contra tortura, garantidos por lei e tratados internacionais, o próprio uso da força policial <a href="https://www.gov.br/secom/pt-br/assuntos/noticias/2025/janeiro/governo-federal-regulamenta-uso-da-forca-policial-e-cria-nucleo-de-combate-ao-crime-organizado?utm_source=chatgpt.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">foi regulamentado</a> pelo Ministério da Justiça e Segurança Pública. A norma, de janeiro deste ano, determinou o uso da arma de fogo como último recurso.

            </p>

            <p>
              Mesmo assim, a política de segurança pública frequentemente utilizada por governantes — em especial, os alinhados com a direita — é marcada pela lógica de extermínio e pelo uso recorrente do estado de exceção nas periferias, que pode se tornar uma sentença de morte para qualquer morador, como José Marcos. 

            </p>

            <p>
              Recentemente, em 28 de outubro, o estado do Rio de Janeiro deflagrou a Operação Contenção, nos complexos da Penha e do Alemão, que culminou na maior chacina policial da história do país. A posição era antes ocupada pelo Carandiru. A operação somou 121 mortos e diversas denúncias de violação de direitos humanos, prisões arbitrárias e violência contra a população. 

            </p>

            {/* Espaço para imagem da Operação Contenção */}
            <div className="my-12">
              <div className="w-full h-auto aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/f7b36f2bb7bcbfac7d8f97c706928e1b.jpeg"
                  alt="Vista aérea dos complexos da Penha e do Alemão no Rio de Janeiro"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3 italic">
                Moradores realizam a identificação de corpos no Complexo da Penha, Zona Norte do Rio de Janeiro, no dia 29 de outubro de 2025. Reprodução/Tomaz Silva/Agência Brasil
              </p>
            </div>

            <p>
              Além do massacre, houve relatos de decapitações e impedimento para a liberação dos corpos. O ápice do sofrimento foi registrado por fotos que estamparam veículos nacionais e internacionais. Nas imagens, moradores e familiares enlutados contabilizam mais de uma centena de óbitos, enquanto tentam reconhecer as vítimas.

            </p>

            <p>
              Um estudo do Grupo de Estudos dos Novos Ilegalismos, da Universidade Federal Fluminense (UFF), <a href="https://www.cnnbrasil.com.br/nacional/apenas-6-5-das-operacoes-policiais-no-rio-foram-em-area-de-milicia-diz-estudo/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">realizado em 2020</a>, apontou que apenas 6,5% das ações policiais na capital fluminense foram realizadas em áreas de milícia em 2019. No último ano, o grupo da UFF e o Instituto Fogo Cruzado destacaram que a chance de um território dominado pelo tráfico registrar confrontos é quase quatro vezes maior que a chance para territórios controlados por milicianos. 

            </p>

            <p>
              É nas favelas e comunidades da periferia que se concentra 8,1% de toda a população do país, com um total de 16,39 milhões de moradores. Os dados do Instituto Brasileiro de Geografia e Estatística (IBGE) também informam que, deste total, <a href="https://almapreta.com.br/sessao/cotidiano/ibge-73-da-populacao-das-favelas-no-brasil-e-negra/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline">73% são pretos ou pardos</a>. A preferência do Estado em utilizar as favelas como palco para a violência policial demonstra um padrão de perfilamento da morte e violência pelo Estado, que atinge desproporcionalmente a comunidade negra. O "combate às drogas" no estado de São Paulo, por exemplo, levou a 31 mil pessoas negras serem enquadradas como traficantes em situações similares à de brancos considerados usuários, conforme apresenta a pesquisa realizada pelo Núcleo de Estudos Raciais do Instituto de Ensino e Pesquisa (Insper).
  

            </p>

            {/* Espaço para imagem sobre necropolítica */}
            <div className="my-12">
              <div className="w-full h-auto bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/34cb41ca77cb91323fe86d063339c6a1.jpeg"
                  alt="Representação conceitual sobre necropolítica e violência estatal"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3 italic">
                Aglomerado de casas das favelas do Complexo do Alemão, Zona Norte do Rio de Janeiro. Reprodução/Tânia Rêgo/Agência Brasil
              </p>
            </div>

            <p>
              Ao formular o conceito de necropolítica, o filósofo camaronês Achille Mbembe, um dos principais pensadores da filosofia política africana contemporânea, observa que, nas sociedades marcadas pela colonização, a escravidão e o racismo, o poder se expressa sobretudo pela capacidade de ditar quem pode viver e quem deve morrer. Nessa lógica, o controle sobre a mortalidade é também uma forma de controle sobre a própria existência.

            </p>

            <p>
              Nas operações policiais que transformam bairros inteiros em zonas de guerra, a soberania assume, portanto, o contorno descrito por Mbembe: o do Estado que age fora da lei, definindo territórios e populações como zonas de morte. Essa forma de governo se consolida na prática cotidiana da violência estatal, que constrói um espaço de exceção permanente, herança direta das estruturas coloniais.

            </p>

            <p>
              O autor identifica o racismo como o mecanismo que torna aceitável o ato de matar, sendo a tecnologia política que define quem deve viver e quem deve morrer. Conforme o pensamento do filósofo, a necropolítica é o exercício de um poder que define a descartabilidade da pessoa negra. Com isso, casos como o de José Marcos e das vítimas das chacinas policiais revelam a persistência de uma política de Estado que não apenas falha em proteger, mas produz a morte como forma de governo.

            </p>

            {/* Segundo subtítulo - 9 parágrafos */}
            <h3 id="contrapontos" className="text-2xl font-semibold text-gray-900 mt-12 mb-6">
              Faltam contrapontos, sobram versões oficiais

            </h3>

            <p>
              Na imprensa, a resposta letal das forças de segurança é justificada, na maioria das vezes, como o resultado direto de um possível conflito armado iniciado por supostos criminosos. A reportagem analisou 50 matérias jornalísticas que noticiaram mortes em decorrência da atividade policial, de cinco grandes veículos brasileiros, entre os anos de 2020 e 2025, para compreender como essas operações são noticiadas nas manchetes. 

            </p>

            <p>
              Em todos os textos, foi possível observar que as narrativas e as informações advindas de órgãos do governo, especialmente de segurança pública, como as Secretarias de Segurança Pública (SSP) ou a própria polícia, possuem um espaço central nos materiais analisados. Essas entidades compõem as chamadas ou "fontes oficiais", que se pronunciam por órgãos mantidos pelo Estado. 

            </p>

            <p>
              Na primeira vez que se tornou manchete no caderno impresso de um dos veículos, no dia 3 de fevereiro, José Marcos, à época ainda não identificado, foi descrito no parecer oficial da Secretaria de Segurança Pública do Estado de São Paulo, e no noticiário, como um criminoso morto em conflito. A história fornecida pelo órgão só foi contestada no jornal três dias depois, quando, pela primeira vez, a família do falecido ganha espaço na história. 

            </p>

            <p>
              Os familiares, logo na primeira reportagem em que aparecem, negaram as acusações e defenderam que o homem não tinha envolvimento com nenhuma atividade ilícita. Desta vez, a narrativa central é a da família, priorizada no texto e destacada no título. Ali, pela primeira vez, o catador ganha um nome e tem sua honra defendida das alegações do Estado.

            </p>

            <p>
              A prevalência desse sistema, onde o órgão informa e o veículo noticia, sem espaço para o contraditório apresentado por fontes como testemunhas ou familiares, esteve presente na maioria das matérias analisadas. Cerca de 73,9% delas não possuía nenhuma fonte além das institucionais, enquanto apenas 26,1% apresentavam mais de uma fonte e contraposições à narrativa dos órgãos de segurança pública.

            </p>

            {/* Espaço para imagem */}
            <div className="my-12">
              <div className="w-full h-auto aspect-[4/3] bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/0878e7cce14fd2387b19993ae9ad3a28.png"
                  alt="Redação jornalística analisando documentos e fontes oficiais"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
            </div>

            <p>
              Aproximadamente 93,4% das notícias utilizavam as instituições como fontes principais, sendo elas dispostas sempre no início do texto. Na estrutura textual jornalística, as informações destacadas como mais relevantes são as primeiras apresentadas, o que pode demonstrar o favorecimento de narrativas tendenciosas sobre letalidade e violência policial, que corroboram com as mortes sistemáticas pela polícia.

            </p>

            {/* Espaço para segunda imagem */}
            <div className="my-12">
              <div className="w-full h-auto bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/a5f015f077968ff1d318e9c8844d09c9.png"
                  alt="Gráfico de análise de fontes jornalísticas"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            <p>
              Nas matérias que possuíam mais de uma fonte, as instituições de direitos humanos e os órgãos públicos de controle externo da atividade policial estiveram presentes em somente 4,34% dos textos, dividindo espaço com as fontes oficiais. Em apenas 2,17%, elas dividiram espaço com as testemunhas ou familiares. 

            </p>

            <p>
              Também foi possível observar que, do total, 73,68% das matérias tratavam de fatos que ocorreram em regiões periféricas. Com os dados, é possível visualizar uma correlação entre a baixa diversidade de fontes e a assimetria na representação das vozes, onde o território é retratado, mas sua população é raramente ouvida. 

            </p>

            {/* Espaço para terceira imagem */}
            <div className="my-12">
              <div className="w-full h-auto bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/09785a0a589a043e94fc8369d42882da.png"
                  alt="Comunidade periférica e a falta de representação nas fontes jornalísticas"
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Terceiro subtítulo - 20 parágrafos em texto corrido */}
            <h3 className="text-2xl font-semibold text-gray-900 mt-12 mb-6">
              "Retroalimentação da violência estatal"
            </h3>

            <p>
              Em entrevista à reportagem, o doutor e diretor da Faculdade de Arquitetura, Artes, Comunicação e Design (FAAC) da Universidade Estadual Paulista Júlio de Mesquita Filho (Unesp), Juarez Tadeu de Paula Xavier, pondera que este fenômeno no jornalismo cumpre o papel de retroalimentação das narrativas do Estado, prática herdada de suas origens coloniais.

            </p>

            <p>
              "A imprensa já nasce no Brasil comprometida com esse processo. Ela surge em 1808, pensando no Jornal do Brasil, Gazeta do Rio de Janeiro, mas ela está sustentada em pressupostos que são anteriores à Constituição da Imprensa do Estado Brasileiro. Quais são esses pressupostos? Primeiro, a descartabilidade humana, provocada durante 350 anos. Segundo, a subcidadania e terceiro o apartheid", explica.


            </p>

            <p>
              Xavier, que é docente e doutor em Jornalismo, afirma que o método moldou um padrão de organização e cobertura da imprensa, baseada majoritariamente nas fontes oficiais. 

            </p>

            {/* Espaço para imagem após parágrafo sobre Xavier */}
            <div className="my-12">
              <div className="w-full h-auto aspect-[27/35] bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/c3bf2e89a7e4aa10b022728d6726ea0c.jpeg"
                  alt="Representação do jornalismo acadêmico e estudos de mídia"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3 italic">
                O jornalista e diretor da Faculdade de Arquitetura, Artes, Comunicação e Design (FAAC) da Unesp Bauru, Juarez Tadeu de Paula Xavier. Reprodução/Instagram/Juarez Xavier
              </p>
            </div>

            <p>
              "Não é um problema isolado desse ou daquele jornal corporativo, mas o modelo todo está sendo retroalimentado e uma das formas de lidar com isso é a partir das fontes oficiais. Ainda são raríssimas as fontes independentes e até mesmo as fontes oficiosas. Então você faz um jornalismo que constrói a narrativa da branquitude, supremacia racial branca, da segregação e assim sucessivamente".

            </p>

            <p>
              O produto resultante dessa narrativa única endossada pelo jornalismo, aponta o diretor, é a legitimação intencional da política de morte e de seu direcionamento às populações marginalizadas.

            </p>

            <p>
              “É a mononarrativa do Estado: eles estavam armados, reagiram, foram mortos e assim sucessivamente, né? Em tese, foram ações legítimas do Estado em conter a horda de bandidos que coloca em perigo o sistema. Por isso as fontes oficiais reforçam essa narrativa. Foram mortos, sim, é uma pena e tal, mas eles colocaram em risco a sociedade”, explica.

            </p>

            <p>
              A jornalista autora do livro “Como nascem os milicianos”, Cecília Olliveira, compreende que é papel da imprensa investigar e exercer uma postura crítica diante das fontes oficiais. No entanto, a comunicadora considera comum observar a reprodução constante de versões institucionais destas operações sem que exista algum tipo de contestação.

            </p>

            <p>
              “Quem detém a informação, detém o controle do que seria dito sobre ela. E claro, o poder público não vai produzir provas contra. Então, um dos efeitos é mascarar a realidade, contribuindo para uma narrativa governamental sobre os fatos. Por exemplo, as operações policiais mal planejadas, com péssimos resultados, são apenas descritas no jornal. Poucas são analisadas”, avaliou.
            </p>

            {/* Espaço para imagem após parágrafo sobre mascaramento da realidade */}
            <div className="my-12">
              <div className="w-full h-auto aspect-[1040/1200] bg-gray-100 rounded-lg overflow-hidden">
                <img 
                  src="https://static.readdy.ai/image/8c697a09a607dd1bcfa59e74f239c1a2/f807cea64eb830171eb9403a55af9621.jpeg"
                  alt="Jornalista Cecília Olliveira, autora do livro Como nascem os milicianos"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              <p className="text-sm text-gray-600 mt-3 italic">
                A jornalista Cecília Olliveira, autora do livro "Como nascem os milicianos". Reprodução/Linkedin/Cecília Olliveira
              </p>
            </div>

            <p>
              Olliveira acredita que este comportamento da imprensa alimenta um ciclo que fortalece a tolerância social às violências do Estado. Ela ressalta que a cobertura de temas ligados à má conduta de policiais e a falta de fiscalização desta função exige contextualização constante. 
            </p>

            <p>
              A espetacularização e a criação de narrativas heroicas em torno de figuras policiais também integram o problema, avalia a escritora. Ao relembrar a ocupação do Morro do Alemão, em 2010, Olliveira destaca a ampla circulação das imagens de traficantes fugindo com fuzis nas costas. Naquele período, programas policialescos chegaram a comemorar as mais de 30 mortes registradas.
            </p>

            <p>
              “Ao mitificar o justiceiro e transformar a violência em espetáculo, parte da imprensa ajuda a perpetuar o medo e a distorcer a percepção pública da segurança”, declara. 

            </p>

            <p>
              Questionada sobre a elegibilidade das Secretarias de Segurança Pública e demais órgãos relacionados às polícias, a autora observou que há um conflito de interesses neste sistema, no qual os jornais passam a prestar o papel de assessoria de imprensa de um órgão governamental. 

            </p>

            <p>
              Juarez Xavier, por sua vez, afirma que a participação de órgãos do gênero pode ser ética quando contrapostas com fontes plurais, como testemunhas ou comissões de direitos humanos. A questão central torna-se em como as fontes serão utilizadas nas notícias.

            </p>

            <p>
              “Se você pluraliza as fontes, sim, é ético. Porque você coloca as contradições nas narrativas. Isso é legal, porque estabelece o contraditório e permite a percepção percebida do fenômeno com percepção real. O problema é quando a narrativa da oficialidade se sobrepõe às outras. Aí deixa de ser ético A questão está em estabelecer contraditórios de comparação entre as fontes”.


            </p>

            <p>
              Ambos entrevistados concordam que parte da solução está nas iniciativas de jornalismo independente legal e eticamente correto. As iniciativas são reconhecidas como uma importante peça para o equilíbrio na cobertura, uma vez que dão mais espaço para a pluralidade das fontes. 

            </p>

            <p>
              “É aquilo que chamamos de jornalismo de possibilidades. Ele pode ser um jornalismo independente, mas ele também pode ser estar nas possibilidades da redação, quando você tem um repórter mais democrático, que faz uma reportagem que pluraliza as fontes, traz as fontes oficiais, oficiosas, que faziam parte do 
sistema e saem denunciando isso”.

            </p>

            <p>
              Olliveira aponta que é fundamental construir estruturas que fortaleçam o jornalismo independente, que podem ser mais visados dada a natureza do trabalho, geralmente investigativo. Este modelo de jornalismo é reconhecido por se contrapor ao modelo tradicional e aprofundar a cobertura sobre justiça racial, ambiental, climática e de gênero.

            </p>

            <p>
              “Para que a classe jornalística avance coletivamente nesse campo, é fundamental construir estruturas que protejam os repórteres e fortaleçam o jornalismo independente - que tem menos recursos e mais visado, dada a natureza do trabalho, geralmente investigativo”.

            </p>

            <p>
              O professor Juarez Xavier conclui que, na cobertura jornalística de segurança pública, a questão da ética está em estabelecer contraditórios de comparação entre as fontes, a fim de ampliar a possibilidade de compreensão do leitor sobre a realidade social. 


            </p>

            <p>
              “Você sai da percepção percebida para a percepção real do leitor. Porque ele pode comparar isso com a realidade dele. Segundo, que acho que é importante em relação às fontes. Você pode ter fonte oficial que a partir de um espaço mais democrático seja muito mais qualificada do ponto de vista dos direitos humanos, por exemplo, e outra que não", completa.

            </p>
          </div>
        </div>
      </div>
    </section>
  );
};